/**
 * The 2D assembly canvas: every room on the current level, each drawn by the
 * room editor's own PlanSvg in export mode (no grid, handles, or dim bars) at
 * the assembly scale, then placed with one SVG translate + rotate. Because the
 * same component draws both, a room in the assembly matches its editor
 * stroke for stroke, minus their object labels (each room's label is the only
 * text here). Over that: a hit path per room (the whole room is the move
 * handle), then, over every footprint, each room's label, the selected
 * room's outline and rotation handle, and the six measure bars. What shows
 * inside the walls, and what each label says, follow the view menu
 * (`PlanViewPreferences`).
 */

import { css } from '@emotion/react';
import { type FloorPlanRoom, resolveScene, roomPlacement, roomPlacementRotateVector, roomPlacementTransform } from '@kaiizen/kai-floor-plan';
import {
  type AssemblerMutableState,
  type AssemblyRoom,
  createEditorState,
  levelRooms,
  modelToScreen,
  placedAssemblyBars,
  type PlacedRoomLabel,
  type PlanLayers,
  planRoomLabels,
  ringsPath,
  roomLabelHitBox,
  useFloorPlanPreferences,
  type Viewport,
} from '@kaiizen/kai-floor-plan-editor-core';
import { memo, useMemo } from 'react';
import { selection as selectionColor } from '../../styles';
import { MeasureBarLines, PlanExportContext, PlanGrid, PlanSvg, RotateHandle } from '../plan';
import { roomHandle, roomRotateHandle } from './assemblyPointer';
import { measuredLineWidth, RoomLabel } from './RoomLabel';

/** Transparent fill (not `none`) so the whole footprint catches the pointer. */
const ROOM_HIT_STYLE = css`
  label: ASSEMBLY_ROOM_HIT_STYLE;
  fill: transparent;
  stroke: none;
  cursor: move;
`;

const ROOM_HIT_READ_ONLY_STYLE = css`
  label: ASSEMBLY_ROOM_HIT_READ_ONLY_STYLE;
  cursor: default;
`;

/** THE selected-room visual: one selection-color outline around the footprint. */
const ROOM_SELECTED_STYLE = css`
  label: ASSEMBLY_ROOM_SELECTED_STYLE;
  fill: none;
  pointer-events: none;
  stroke: ${selectionColor};
  stroke-width: 4;
  stroke-linejoin: round;
`;

/** One room's plan, rendered at the assembly scale in the room's own frame (origin at its local 0,0). */
const AssemblyRoomPlan = memo(
  function AssemblyRoomPlan(
    { room, scale, stageW, stageH, layers }: { room: FloorPlanRoom; scale: number; stageW: number; stageH: number; layers: PlanLayers },
  ) {
    const roomState = useMemo(() => {
      const st = createEditorState(room);
      st.viewport = { scale, offset: { x: 0, y: 0 } };
      st.layers = layers;
      return st;
    }, [room, scale, layers]);
    return <PlanSvg st={roomState} stageW={stageW} stageH={stageH} showObjectLabels={false} />;
  },
);

/** SVG path of the room's footprint (outer ring + holes) in screen space. */
function footprintPath(room: FloorPlanRoom, vp: Viewport): string {
  const toPlan = roomPlacementTransform(room);
  return ringsPath(resolveScene(room).roomSolid.flat().map(ring => ring.map(p => modelToScreen(vp, toPlan(p)))));
}

/** The SVG transform placing a room drawn at its local origin into the assembly. */
export function roomPlanTransform(room: FloorPlanRoom, vp: Viewport): string {
  const { x, y, rotation } = roomPlacement(room);
  const origin = modelToScreen(vp, { x, y });
  const cx = (room.width / 2) * vp.scale, cy = (room.length / 2) * vp.scale;
  return `translate(${origin.x} ${origin.y}) rotate(${rotation} ${cx} ${cy})`;
}

/** Just the given rooms, each drawn in export mode and placed by transform (the minimap's and the exports' content too). */
export function AssemblyRoomsLayer(
  { rooms, vp, stageW, stageH, layers }: { rooms: readonly AssemblyRoom[]; vp: Viewport; stageW: number; stageH: number; layers: PlanLayers },
): React.JSX.Element {
  return (
    <PlanExportContext.Provider value={true}>
      {rooms.map(({ id, room }) => (
        <g key={id} data-room-id={id} data-layer='assembly-room' transform={roomPlanTransform(room, vp)}>
          <AssemblyRoomPlan room={room} scale={vp.scale} stageW={stageW} stageH={stageH} layers={layers} />
        </g>
      ))}
    </PlanExportContext.Provider>
  );
}

export interface AssemblyPlanSvgProps {
  st: AssemblerMutableState;
  stageW: number;
  stageH: number;
}

export function AssemblyPlanSvg({ st, stageW, stageH }: AssemblyPlanSvgProps): React.JSX.Element {
  const view = useFloorPlanPreferences();
  const vp = st.viewport;
  const rooms = levelRooms(st);
  return (
    <>
      <PlanGrid vp={vp} stageW={stageW} stageH={stageH} />
      <AssemblyRoomsLayer rooms={rooms} vp={vp} stageW={stageW} stageH={stageH} layers={view.layers} />
      {rooms.map(({ id, room }) => (
        <path
          key={id}
          css={[ROOM_HIT_STYLE, st.readOnly && ROOM_HIT_READ_ONLY_STYLE]}
          data-layer='assembly-room-hit'
          data-handle={roomHandle(id)}
          d={footprintPath(room, vp)}
          fillRule='evenodd'
        />
      ))}
      {planRoomLabels(st, view).map(label => <RoomOverlay key={label.assemblyRoom.id} label={label} readOnly={st.readOnly} vp={vp} />)}
      <MeasureBarLines placed={placedAssemblyBars(st, stageW, stageH)} />
    </>
  );
}

/**
 * What a room draws over every room's footprint: the selected outline, its
 * label with a hit rect behind it (a press on the label is a press on the
 * room, even where the label reaches past it over a neighbor), and the
 * selected room's rotation handle. Drawn after all the footprints, so no
 * neighbor's footprint covers them.
 */
function RoomOverlay({ label, readOnly, vp }: { label: PlacedRoomLabel; readOnly: boolean; vp: Viewport }): React.JSX.Element {
  const { assemblyRoom: { id, room }, center, selectedId } = label;
  const active = id === selectedId;
  const labelBox = roomLabelHitBox(label, { lineWidth: measuredLineWidth });
  return (
    <g data-layer='assembly-room-overlay' data-room-overlay={id}>
      {active && <path css={ROOM_SELECTED_STYLE} d={footprintPath(room, vp)} fillRule='evenodd' />}
      {labelBox && <rect css={[ROOM_HIT_STYLE, readOnly && ROOM_HIT_READ_ONLY_STYLE]} data-handle={roomHandle(id)} {...labelBox} />}
      <RoomLabel {...label} />
      {active && !readOnly && (
        <RotateHandle
          center={center}
          rightSideMidpoint={modelToScreen(vp, roomPlacementTransform(room)({ x: room.width, y: room.length / 2 }))}
          outward={roomPlacementRotateVector(room, { x: 1, y: 0 })}
          handle={roomRotateHandle(id)}
        />
      )}
    </g>
  );
}

export type { AssemblyRoom };
